import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Circle, CircleCheckBig, Compass } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import {
  DEFAULT_STEP_DURATION,
  StepProgressRunner,
  runStepProgress,
} from '@/components/ui/multiStepLoaderProgress.ts';

export interface LoaderStep {
  text: string;
}

interface MultiStepLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: LoaderStep[];
  loading: boolean;
  /**
   * Praca w tle się udała. Do tego momentu oś stoi na przedostatnim kroku —
   * „gotowe” nie może paść, zanim serwer to potwierdzi.
   */
  done?: boolean;
  stepDuration?: number;
  /** Ostatni krok odstał swoje; dopiero tu wolno przejść dalej. */
  onFinished?: () => void;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
};

/** Odstęp między wierszami listy; ta sama liczba przesuwa całą listę w górę. */
const ROW_OFFSET_REM = 2.5;

const VERTICAL_FADE =
  'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)';

/**
 * Pełnoekranowa nakładka na czas dłuższej operacji: lista kroków sunąca w górę,
 * bieżący w bursztynie, zrobione z ptaszkiem.
 *
 * Nie jest paskiem postępu — kroki idą z zegara, nie z pracy, więc tekst ma
 * mówić o czymś prawdziwym tylko na początku i na końcu (pierwszy krok i ten po
 * `done`). Środek jest po to, żeby czekanie miało rytm.
 */
const MultiStepLoader = ({
  loading,
  steps,
  ...props
}: MultiStepLoaderProps) => {
  // Oś czasu żyje w zamontowanym poddrzewie, więc każde otwarcie zaczyna od
  // pierwszego kroku samo z siebie — bez zerowania stanu w efekcie.
  if (!loading || steps.length === 0) return null;

  return <MultiStepLoaderOverlay steps={steps} {...props} />;
};

type MultiStepLoaderOverlayProps = Omit<MultiStepLoaderProps, 'loading'>;

const MultiStepLoaderOverlay = ({
  steps,
  done = false,
  stepDuration = DEFAULT_STEP_DURATION,
  onFinished,
  className,
  ...props
}: MultiStepLoaderOverlayProps) => {
  const [index, setIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [announced, setAnnounced] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [host] = useState(() => document.createElement('div'));

  const runnerRef = useRef<StepProgressRunner | null>(null);
  // Sukces bywa już zgłoszony, zanim nakładka wystartuje, a `done` w zależnościach
  // efektu restartowałoby oś od pierwszego kroku.
  const doneRef = useRef(done);
  const onFinishedRef = useRef(onFinished);

  const stepCount = steps.length;

  // Pierwszy efekt w kolejności, więc oś poniżej czyta już świeże wartości.
  useEffect(() => {
    doneRef.current = done;
    onFinishedRef.current = onFinished;
  });

  useEffect(() => {
    const runner = runStepProgress({
      stepCount,
      stepDuration,
      onStep: setIndex,
      onFinished: () => {
        setFinished(true);
        onFinishedRef.current?.();
      },
    });
    runnerRef.current = runner;
    if (doneRef.current) runner.markDone();

    return () => {
      runner.stop();
      runnerRef.current = null;
    };
  }, [stepCount, stepDuration]);

  useEffect(() => {
    if (done) runnerRef.current?.markDone();
  }, [done]);

  /**
   * Nakładka jest modalna, więc reszta strony musi wypaść z tabulatora —
   * inaczej klawiaturą da się wejść w formularz, którego już nie widać.
   * `inert` zdejmujemy tylko z tego, co sami nim oznaczyliśmy: cudzych nakładek
   * nie odblokowujemy przy wyjściu.
   */
  useEffect(() => {
    document.body.appendChild(host);

    const inerted = Array.from(document.body.children).filter(
      (element): element is HTMLElement =>
        element !== host && element instanceof HTMLElement && !element.inert,
    );
    inerted.forEach((element) => (element.inert = true));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      inerted.forEach((element) => (element.inert = false));
      document.body.style.overflow = previousOverflow;
      host.remove();
    };
  }, [host]);

  const firstStepText = steps[0].text;
  const lastStepText = steps[stepCount - 1].text;

  // Czytnik ekranu dostaje dwa zdania na całą operację: co się zaczęło i że się
  // skończyło. Kroki sunące co pół sekundy zagłuszyłyby wszystko inne. Region
  // musi istnieć pusty, zanim dostanie treść — inaczej część czytników milczy.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnnounced(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const announcement = finished ? lastStepText : announced ? firstStepText : '';

  const currentStep = steps[Math.min(index, stepCount - 1)];

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={firstStepText}
      aria-busy={!finished}
      className={cn(
        'fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-basic-white/90 px-6 backdrop-blur-sm',
        className,
      )}
      {...props}
    >
      <p className="sr-only" role="status">
        {announcement}
      </p>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 45%, rgba(242, 173, 13, 0.12), transparent 62%)',
        }}
      />

      <div className="relative flex w-full max-w-sm flex-col items-center gap-6">
        <Compass
          aria-hidden
          className={cn(
            'size-10 text-accent-600',
            !prefersReducedMotion && 'animate-compass-needle',
          )}
        />

        {prefersReducedMotion ? (
          <p
            aria-hidden
            className="text-center text-[15px] font-semibold text-accent-700"
          >
            {currentStep.text}
          </p>
        ) : (
          <ul
            aria-hidden
            className="relative h-44 w-full"
            style={{
              // Maska jako styl, nie klasa: `mask-image` bez prefiksu nie
              // działa w starszym WebKicie. Tylko pionowo — promieniowa
              // wygaszała też boki, więc na wąskim ekranie początek i koniec
              // każdego kroku robiły się blade.
              maskImage: VERTICAL_FADE,
              WebkitMaskImage: VERTICAL_FADE,
            }}
          >
            {steps.map((step, stepIndex) => {
              const distance = stepIndex - index;
              // Ostatni krok mówi „gotowe”, a oś dochodzi do niego dopiero po
              // potwierdzeniu z serwera — pusty krążek przy takim napisie
              // przeczyłby sam sobie, więc ptaszek pada od razu.
              const isLastReached =
                stepIndex === index && stepIndex === stepCount - 1;
              const isDone =
                stepIndex < index ||
                isLastReached ||
                (finished && stepIndex === index);
              const isCurrent = stepIndex === index && !isDone;

              return (
                <li
                  key={stepIndex}
                  className="absolute inset-x-0 top-1/2 flex items-start justify-center gap-2 transition-all duration-500 ease-out"
                  style={{
                    transform: `translateY(calc(-50% + ${distance * ROW_OFFSET_REM}rem))`,
                    opacity: Math.max(0, 1 - Math.abs(distance) * 0.32),
                  }}
                >
                  {isDone ? (
                    <CircleCheckBig className="mt-0.5 size-4 shrink-0 text-success-300" />
                  ) : (
                    <Circle
                      className={cn(
                        'mt-0.5 size-4 shrink-0',
                        isCurrent ? 'text-accent-600' : 'text-neutral-300',
                      )}
                    />
                  )}
                  <span
                    className={cn(
                      'min-w-0 text-center text-[15px]',
                      isCurrent && 'font-semibold text-accent-700',
                      isLastReached && 'font-semibold text-success-300',
                      !isCurrent && !isLastReached && 'text-neutral-600',
                    )}
                  >
                    {step.text}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>,
    host,
  );
};

export default MultiStepLoader;
